import {Button, Divider, Grid} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AntibodyDomainSearchInput} from 'antibodies/actions/AntibodyDomainSearchInput';
import AntibodyComponentSvg, {
  AntibodyComponentType,
} from 'antibodies/nested-data-creation/antibody-component-visualization/AntibodyComponentSvg';
import {TranslationParameters} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {ConstantAaDomainEntity} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import SearchForDnaPairsModal from 'antibodies/nested-data-creation/panes/SearchForDnaPairsModal';
import {pluralize} from 'BenchlingUtil';
import {CodonTranslationTable} from 'biojs/BioConstants';
import GeneticCodeDropdown from 'molbio/translations/GeneticCodeDropdown';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import runModal from 'ui-library/runModal';

const ANTIBODY_COMPONENT_SVG_SIZE = 75;

export interface GetDomainsPaneProps {
  selectedPairIds: Array<string>;
  setSelectedPairIds: (selectedPairIds: Array<string>) => void;
  aaDomainTranslationParameters: TranslationParameters;
  updateAaDomainTranslationParameters: (translationParametersUpdate: Partial<TranslationParameters>) => void;
  constantAaDomainEntities: Record<string, ConstantAaDomainEntity>;
  selectConstantDomainEntity: (constantDomainEntity: ConstantAaDomainEntity) => void;
}

/**
 * Nested data creation step that defines configuration for creating AA variable domains and chains
 * and selecting DNA variable domain pair entities.
 */
export default function GetDomainsPane({
  selectedPairIds,
  setSelectedPairIds,
  aaDomainTranslationParameters,
  updateAaDomainTranslationParameters,
  constantAaDomainEntities,
  selectConstantDomainEntity,
}: GetDomainsPaneProps): JSX.Element {
  async function openSearchModal(): Promise<void> {
    const newSelectedPairIds = await runModal(SearchForDnaPairsModal, {});
    if (newSelectedPairIds != null) {
      setSelectedPairIds(newSelectedPairIds);
    }
  }

  return (
    <div>
      <div className={css(styles.componentSection)}>
        <AntibodyComponentSvg
          componentType={AntibodyComponentType.DNA_DOMAIN}
          size={ANTIBODY_COMPONENT_SVG_SIZE}
        />
        <div>
          <h3 className={css(styles.sectionTitle)}>DNA variable domains</h3>
          <p>Bring in DNA variable domains by selecting pair entities</p>
          {selectedPairIds.length === 0 ? (
            <Button
              leftIcon={<BenchlingIcon icon="magnifying-glass" />}
              onClick={openSearchModal}
              size="md"
              variant="tertiary"
            >
              Search for pair entities
            </Button>
          ) : (
            <Chip
              size={ChipSize.SMALL}
              text={`${selectedPairIds.length} ${pluralize(selectedPairIds.length, 'pair')} selected`}
              onRemove={() => {
                setSelectedPairIds([]);
              }}
            />
          )}
        </div>
      </div>
      <Divider my="md" />
      <div className={css(styles.componentSection)}>
        <AntibodyComponentSvg
          componentType={AntibodyComponentType.AA_DOMAIN}
          size={ANTIBODY_COMPONENT_SVG_SIZE}
        />
        <div className={css(styles.componentSectionContent)}>
          <h3 className={css(styles.sectionTitle)}>AA variable domains</h3>
          <p>Configure parameters for translating DNA domains to AA domains</p>
          <div className={css(styles.aaDomainParamsContainer)}>
            <div className={css(styles.geneticCode)}>
              <FieldLabel label="Genetic code" />
              <GeneticCodeDropdown
                geneticCode={aaDomainTranslationParameters.geneticCode}
                onSelectGeneticCode={(geneticCode: CodonTranslationTable) => {
                  updateAaDomainTranslationParameters({geneticCode});
                }}
                disabled={false}
              />
            </div>
            <div className={css(styles.aaDomainParam)}>
              <FieldLabel label="Start codon" htmlFor="require-start-codon" />
              <CheckboxInput
                id="require-start-codon"
                checked={aaDomainTranslationParameters.requireStartCodon}
                label="Require a start codon"
                onChange={(e) => {
                  updateAaDomainTranslationParameters({
                    requireStartCodon: e.target.checked,
                  });
                }}
              />
            </div>
            <div className={css(styles.aaDomainParam)}>
              <FieldLabel label="Stop codon" htmlFor="disallow-premature-stop-codons" />
              <CheckboxInput
                id="disallow-premature-stop-codons"
                checked={aaDomainTranslationParameters.disallowPrematureStopCodons}
                label="Don't allow premature stop codon"
                onChange={(e) => {
                  updateAaDomainTranslationParameters({
                    disallowPrematureStopCodons: e.target.checked,
                  });
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <Divider my="md" />
      <div className={css(styles.componentSection)}>
        <AntibodyComponentSvg
          componentType={AntibodyComponentType.AA_CHAIN}
          size={ANTIBODY_COMPONENT_SVG_SIZE}
        />
        <div className={css(styles.componentSectionContent)}>
          <h3 className={css(styles.sectionTitle)}>AA chains</h3>
          <p>Set constant domains for AA chains by selecting existing entities</p>
          <Grid>
            {Object.entries(constantAaDomainEntities).map(([distinctDomainKey, constantDomainEntity]) => (
              <Grid.Col span={4} key={distinctDomainKey}>
                <AntibodyDomainSearchInput
                  key={distinctDomainKey}
                  disabled={false}
                  distinctDomain={constantDomainEntity}
                  selectedProtein={constantDomainEntity.aaSequenceId}
                  setSelectedProtein={(newSelectedProteinId) => {
                    selectConstantDomainEntity({
                      ...constantDomainEntity,
                      aaSequenceId: newSelectedProteinId,
                    });
                  }}
                  // TODO(BNCH-133467): Filter to only AA sequences of the correct domain schema
                  linkedSchemaId={null}
                  showSwatch={false}
                />
              </Grid.Col>
            ))}
          </Grid>
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  componentSection: {
    display: 'flex',
    gap: '16px',
    padding: '16px 0',
  },
  componentSectionContent: {
    flexGrow: 1,
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: 4,
  },
  aaDomainParamsContainer: {
    display: 'flex',
    gap: 8,
  },
  geneticCode: {
    flexBasis: '33%',
  },
  aaDomainParam: {
    flexGrow: 1,
  },
});
